import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { buildApiUrl } from '../config';

const PoliticianProfile = () => {
  const { politicianId } = useParams();
  const navigate = useNavigate();
  const [politician, setPolitician] = useState(null);
  const [votingHistory, setVotingHistory] = useState([]);
  const [votingStats, setVotingStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, yes, no, abstain, absent

  useEffect(() => {
    const fetchPoliticianProfile = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch politician details
        const politicianResponse = await fetch(buildApiUrl(`/api/politicians/${politicianId}`));
        if (!politicianResponse.ok) {
          throw new Error('Failed to fetch politician details');
        }
        const politicianData = await politicianResponse.json();
        setPolitician(politicianData);

        // Fetch voting history and stats (admin endpoint; use env-aware URL)
        const votingResponse = await fetch(buildApiUrl(`/api/admin/politician/${politicianId}/voting-history`));
        if (votingResponse.ok) {
          const votingData = await votingResponse.json();
          setVotingHistory(votingData.votingHistory || []);
          setVotingStats(votingData.votingStats || null);
        }

      } catch (err) {
        console.error('Error fetching politician profile:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (politicianId) {
      fetchPoliticianProfile();
    }
  }, [politicianId]);

  const getVoteColor = (vote) => {
    switch (vote?.toLowerCase()) {
      case 'yes': case 'aye': return 'text-green-600 bg-green-100';
      case 'no': case 'nay': return 'text-red-600 bg-red-100';
      case 'abstain': case 'abstention': return 'text-yellow-600 bg-yellow-100';
      case 'absent': case 'not present': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getVoteIcon = (vote) => {
    switch (vote?.toLowerCase()) {
      case 'yes': case 'aye': return '✅';
      case 'no': case 'nay': return '❌';
      case 'abstain': case 'abstention': return '⚪';
      case 'absent': case 'not present': return '🚫';
      default: return '❓';
    }
  };

  // Filter votes based on selected filters
  const filteredVotes = votingHistory.filter(vote => {
    return filter === 'all' || vote.vote?.toLowerCase() === filter.toLowerCase();
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading politician profile...</p>
        </div>
      </div>
    );
  }

  if (error || !politician) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-3xl">⚠️</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Politician Not Found</h2>
          <p className="text-gray-600 mb-6">{error || 'The requested politician profile could not be found.'}</p>
          <button
            onClick={() => navigate('/')}
            className="btn-primary"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
            <div className="text-sm text-gray-500">
              Politician Profile
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Politician Header */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-start space-x-6">
            <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-3xl font-bold">
              {politician.name?.charAt(0) || '?'}
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{politician.name}</h1>
              <p className="text-lg text-gray-600 mb-4">{politician.title || 'Politician'}</p>
              
              <div className="flex items-center space-x-6 text-sm text-gray-500">
                <span className="flex items-center">
                  <span className="mr-2">📍</span>
                  {politician.locality || 'Unknown Location'}
                </span>
                <span className="flex items-center">
                  <span className="mr-2">🏛️</span>
                  {politician.party || 'Independent'}
                </span>
                <span className="flex items-center">
                  <span className="mr-2">🗳️</span>
                  {votingStats?.totalVotes || votingHistory.length} total votes
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Voting Statistics */}
        {votingStats && (
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Voting Statistics</h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 mb-2">{votingStats.totalVotes}</div>
                <div className="text-sm text-gray-600">Total Votes</div>
              </div>
              
              {Object.entries(votingStats.voteBreakdown || {}).map(([voteType, count]) => (
                <div key={voteType} className="text-center">
                  <div className={`text-3xl font-bold mb-2 ${getVoteColor(voteType).split(' ')[0]}`}>
                    {count}
                  </div>
                  <div className="text-sm text-gray-600 capitalize">{voteType}</div>
                </div>
              ))}
            </div>
            
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="text-center">
                <span className={`px-4 py-2 rounded-full text-sm font-medium ${
                  votingStats.participationRate === 'Active' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {votingStats.participationRate}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Filter Votes</h3>
          
          <div className="flex flex-wrap gap-4">
            {/* Vote Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Vote Type</label>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="input-modern"
              >
                <option value="all">All Votes</option>
                <option value="yes">Yes/Aye</option>
                <option value="no">No/Nay</option>
                <option value="abstain">Abstain</option>
                <option value="absent">Absent</option>
              </select>
            </div>

          </div>
        </div>

        {/* Voting History */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Voting History</h2>
            <div className="text-sm text-gray-500">
              Showing {filteredVotes.length} of {votingHistory.length} votes
            </div>
          </div>
          
          {filteredVotes.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🗳️</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">No Votes Found</h3>
              <p className="text-gray-600">No votes match the current filters.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {filteredVotes.map((vote, index) => (
                <div
                  key={vote._id || index}
                  className="group relative bg-gradient-to-r from-white to-gray-50 border-2 border-gray-200 rounded-2xl p-6 hover:shadow-2xl hover:border-blue-300 transition-all duration-300 hover:scale-[1.02]"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  {/* Colored accent bar */}
                  <div className={`absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl ${
                    vote.vote?.toLowerCase() === 'yes' || vote.vote?.toLowerCase() === 'aye' ? 'bg-gradient-to-b from-green-400 to-green-600' :
                    vote.vote?.toLowerCase() === 'no' || vote.vote?.toLowerCase() === 'nay' ? 'bg-gradient-to-b from-red-400 to-red-600' :
                    vote.vote?.toLowerCase() === 'abstain' ? 'bg-gradient-to-b from-yellow-400 to-yellow-600' :
                    'bg-gradient-to-b from-gray-300 to-gray-500'
                  }`}></div>

                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4 flex-1">
                      {/* Vote Icon */}
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-2xl shadow-md ${
                        vote.vote?.toLowerCase() === 'yes' || vote.vote?.toLowerCase() === 'aye' ? 'bg-green-100' :
                        vote.vote?.toLowerCase() === 'no' || vote.vote?.toLowerCase() === 'nay' ? 'bg-red-100' :
                        vote.vote?.toLowerCase() === 'abstain' ? 'bg-yellow-100' :
                        'bg-gray-100'
                      }`}>
                        {getVoteIcon(vote.vote)}
                      </div>

                      {/* Verdict Info */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-bold text-lg text-gray-900 group-hover:text-blue-600 transition-colors pr-4">
                            {vote.verdictTitle || vote.verdict?.title || 'Unknown Verdict'}
                          </h3>
                          <div className={`px-4 py-2 rounded-xl text-sm font-bold shadow-sm ${getVoteColor(vote.vote)}`}>
                            {vote.vote?.toUpperCase()}
                          </div>
                        </div>

                        {(vote.verdictDescription || vote.verdict?.description) && (
                          <p className="text-sm text-gray-700 mb-3 line-clamp-2">
                            {vote.verdictDescription || vote.verdict?.description}
                          </p>
                        )}

                        <div className="flex flex-wrap items-center gap-4 text-sm">
                          <span className="flex items-center text-gray-600 bg-white px-3 py-1 rounded-lg shadow-sm">
                            📅
                            <span className="ml-2 font-medium">{new Date(vote.matchedAt || vote.createdAt || vote.verdict?.meetingDate).toLocaleDateString()}</span>
                          </span>

                          {(vote.verdictOutcome || vote.verdict?.outcome) && (
                            <span className={`flex items-center px-3 py-1 rounded-lg shadow-sm font-medium ${
                              (vote.verdictOutcome || vote.verdict?.outcome) === 'passed' ? 'bg-green-100 text-green-800' :
                              (vote.verdictOutcome || vote.verdict?.outcome) === 'failed' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {(vote.verdictOutcome || vote.verdict?.outcome) === 'passed' ? '✅ Passed' :
                               (vote.verdictOutcome || vote.verdict?.outcome) === 'failed' ? '❌ Failed' : (vote.verdictOutcome || vote.verdict?.outcome)}
                            </span>
                          )}
                        </div>

                        {/* Vote Counts if available */}
                        {(vote.verdictVoteCounts || vote.verdict?.voteCounts) && (
                          <div className="mt-3 flex items-center space-x-4 text-xs">
                            <span className="text-gray-500">Vote Breakdown:</span>
                            <span className="text-green-700 font-semibold">✓ {(vote.verdictVoteCounts || vote.verdict?.voteCounts)?.yes || 0} Yes</span>
                            <span className="text-red-700 font-semibold">✗ {(vote.verdictVoteCounts || vote.verdict?.voteCounts)?.no || 0} No</span>
                            {((vote.verdictVoteCounts || vote.verdict?.voteCounts)?.abstain || 0) > 0 && (
                              <span className="text-yellow-700 font-semibold">⊝ {(vote.verdictVoteCounts || vote.verdict?.voteCounts)?.abstain} Abstain</span>
                            )}
                          </div>
                        )}

                        {/* Tags */}
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          {vote.manuallyEdited && (
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">
                              ✏️ Edited
                            </span>
                          )}

                          {vote.resolvedBy === 'admin' && (
                            <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full font-medium">
                              👤 Manual Entry
                            </span>
                          )}

                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default PoliticianProfile;
